import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import {
    Dialog,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../../components/ui/Dialog";
import { Button } from "../../components/ui/Button";
import { useAuth } from "src/contexts/AuthContext";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

interface CreditConfirmationDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

// Move ModalWrapper outside the main component to fix "Component is not a function" error
const ModalWrapper = ({
    isOpen,
    onClose,
    onConfirm,
    totalInterviewCount,
    freeInterviewCount,
    boughtInterviewCount,
    handleSubscribe,
}: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    totalInterviewCount: number;
    freeInterviewCount: number;
    boughtInterviewCount: number;
    handleSubscribe: () => void;
}) => (
    <div
        className="fixed inset-0 overflow-y-auto overflow-x-hidden flex z-[99999]"
        style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            zIndex: 99999,
            padding: "1rem",
        }}>
        {/* Backdrop */}
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(4px)",
            }}
            onClick={onClose}
        />

        {/* Modal Content - positioned relative to current scroll position */}
        <div
            className="bg-white rounded-xl p-5 shadow-2xl max-w-sm w-full relative z-[100000] mx-4 my-auto"
            style={{
                position: "relative",
                zIndex: 100000,
                maxWidth: "24rem",
                width: "calc(100% - 2rem)",
                backgroundColor: "white",
                borderRadius: "0.75rem",
                padding: "1.25rem",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                marginTop: "calc(20vh)",
            }}
            onClick={(e) => e.stopPropagation()}>
            {totalInterviewCount === 0 ? (
                <>
                    <div className="text-center">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}>
                            <h2 className="text-[28px] font-bold text-red-600 mb-5 tracking-tight flex items-center justify-center gap-3">
                                <span
                                    className="text-2xl"
                                    style={{
                                        color: "var(--color-primary)",
                                    }}>
                                    ❌
                                </span>
                                Tokens Run Out
                            </h2>
                        </motion.div>
                    </div>

                    <div className="flex flex-col items-center space-y-6">
                        <motion.div
                            className="w-32 h-32"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.7 }}>
                            <DotLottieReact
                                src="https://lottie.host/b53cfe5f-97bb-459d-86b2-f9838448ee80/WtOO1WOLfk.lottie"
                                autoplay
                            />
                        </motion.div>

                        <motion.div
                            className="text-center space-y-5"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3 }}>
                            <p className="text-xl font-semibold text-gray-800">
                                😢 You've used all your interview credits
                            </p>
                            <div className="text-base text-gray-600">
                                <p className="font-semibold mb-3 text-gray-800">
                                    🚀 Subscribe now to unlock:
                                </p>
                                <ul className="space-y-3 list-none pl-0">
                                    <li className="p-2.5 bg-blue-50 rounded-lg border border-blue-100 transition-transform hover:scale-105">
                                        🎯{" "}
                                        <span className="font-semibold text-blue-700">
                                            Unlimited
                                        </span>{" "}
                                        mock interviews
                                    </li>
                                    <li className="p-2.5 bg-blue-50 rounded-lg border border-blue-100 transition-transform hover:scale-105">
                                        💡 Smart{" "}
                                        <span className="font-semibold text-blue-700">
                                            insights
                                        </span>{" "}
                                        to accelerate your progress
                                    </li>
                                </ul>
                            </div>
                        </motion.div>

                        <motion.div
                            className="flex gap-3 w-full justify-center pt-4"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}>
                            <Button
                                variant="outline"
                                onClick={onClose}
                                className="min-w-[120px] h-10 text-sm hover:bg-gray-100 transition-colors"
                                style={{
                                    color: "var(--color-primary)",
                                    borderColor: "var(--color-primary)",
                                }}>
                                Maybe Later
                            </Button>
                            <Button
                                onClick={handleSubscribe}
                                className="min-w-[120px] h-10 text-sm"
                                style={{
                                    backgroundColor: "var(--color-primary)",
                                    color: "white",
                                }}>
                                Subscribe Now
                            </Button>
                        </motion.div>
                    </div>
                </>
            ) : (
                <>
                    <div className="text-center">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">
                                Ready to Begin?
                            </h2>
                            <p className="text-base text-gray-600">
                                Confirm to start your interview session
                            </p>
                        </motion.div>
                    </div>

                    <div className="flex flex-col items-center space-y-6">
                        <DotLottieReact
                            src="https://lottie.host/46e450df-6678-4da5-be34-1d9aae84569e/CYkf8XRMqg.lottie"
                            speed={0.5}
                            autoplay
                        />

                        <motion.div
                            className="text-center space-y-4"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3 }}>
                            <div className="text-lg text-gray-700 mb-3">
                                Remaining Credits:{" "}
                                <span
                                    className="font-bold"
                                    style={{
                                        color: "var(--color-primary)",
                                        fontSize: "1.25rem",
                                    }}>
                                    {totalInterviewCount}
                                </span>
                            </div>
                            {boughtInterviewCount > 0 && (
                                <div className="text-sm text-gray-600">
                                    ({freeInterviewCount} free +{" "}
                                    {boughtInterviewCount} premium)
                                </div>
                            )}
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <p className="text-sm text-gray-700">
                                    Starting this interview will consume{" "}
                                    <span
                                        className="font-semibold"
                                        style={{
                                            color: "var(--color-primary)",
                                        }}>
                                        one interview credit
                                    </span>
                                    . Credits won't be refunded for incomplete
                                    interviews.
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            className="flex gap-3 w-full justify-center pt-4"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}>
                            <Button
                                variant="outline"
                                onClick={onClose}
                                className="min-w-[120px] h-10 text-sm hover:bg-gray-100 transition-colors"
                                style={{
                                    color: "var(--color-primary)",
                                    borderColor: "var(--color-primary)",
                                }}>
                                Cancel
                            </Button>
                            <Button
                                onClick={onConfirm}
                                className="min-w-[120px] h-10 text-sm"
                                style={{
                                    backgroundColor: "var(--color-primary)",
                                    color: "white",
                                }}>
                                Start Interview
                            </Button>
                        </motion.div>
                    </div>
                </>
            )}
        </div>
    </div>
);

const CreditConfirmationDialog = React.memo(function CreditConfirmationDialog({
    isOpen,
    onClose,
    onConfirm,
}: CreditConfirmationDialogProps) {
    const { userDoc } = useAuth();

    // Memoize computed values
    const { freeInterviewCount, boughtInterviewCount, totalInterviewCount } =
        React.useMemo(() => {
            const freeCount = userDoc?.interviewCount || 0;
            const now = new Date();
            const boughtInterviews = userDoc?.boughtInterviews || [];
            const boughtCount = boughtInterviews.reduce(
                (
                    total: number,
                    item: { count?: number; expiresAt?: { _seconds: number } }
                ) => {
                    const expiresAt = item.expiresAt?._seconds
                        ? new Date(item.expiresAt._seconds * 1000)
                        : null;
                    if (!expiresAt || expiresAt > now) {
                        return total + (item.count || 0);
                    }
                    return total;
                },
                0
            );
            return {
                freeInterviewCount: freeCount,
                boughtInterviewCount: boughtCount,
                totalInterviewCount: freeCount + boughtCount,
            };
        }, [userDoc]);

    // Memoize handlers
    const handleSubscribe = React.useCallback(() => {
        window.open("/subscription", "_blank");
        onClose();
    }, [onClose]);

    // Scroll to top when dialog opens
    useEffect(() => {
        if (isOpen) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [isOpen]);

    // If dialog is not open, don't render anything
    if (!isOpen) return null;

    // Create a portal to render the modal at the root level
    return (
        <>
            {typeof document !== "undefined" &&
                ReactDOM.createPortal(
                    <ModalWrapper
                        isOpen={isOpen}
                        onClose={onClose}
                        onConfirm={onConfirm}
                        totalInterviewCount={totalInterviewCount}
                        freeInterviewCount={freeInterviewCount}
                        boughtInterviewCount={boughtInterviewCount}
                        handleSubscribe={handleSubscribe}
                    />,
                    document.body
                )}
        </>
    );
});

export default CreditConfirmationDialog;
